import React from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { Link } from 'react-router-dom'
import HeaderSix from '../components/header/HeaderSix'
import FooterSix from '../components/footer/FooterSix'
import ScrollAnimation from '../components/motion/ScrollAnimation'

function CallCenterService() {
    const cards = [
        {
            num: '01',
            title: 'Inbound Customer Support',
            text: 'Every inbound call answered by an agent trained on your products, your policies, and your brand voice. General inquiries, order management, account questions, complaint resolution, returns and refunds are handled completely, without escalating everything to your internal team.',
        },
        {
            num: '02',
            title: 'Appointment Scheduling & Management',
            text: 'New bookings, rescheduling, cancellations, reminders, and waitlist management, integrated directly with your calendar or booking system. No-show rates drop by 30 to 50 percent when reminders are handled systematically and every cancellation immediately triggers a waitlist call.',
        },
        {
            num: '03',
            title: 'Outbound Sales & Lead Follow-Up',
            text: 'Warm lead follow-up while interest is still high. Cold outreach to qualified prospect lists. Appointment setting that puts only properly qualified leads in front of your sales team. Win-back campaigns for lapsed customers who just needed someone to reach out.',
        },
        {
            num: '04',
            title: 'Technical & Helpdesk Support',
            text: 'Tier one and tier two support for software products, technology platforms, and service businesses with complex offerings. Real troubleshooting not just logging tickets and apologizing. Integrated with your existing support and project management tools.',
        },
        {
            num: '05',
            title: 'Customer Retention & Churn Prevention',
            text: 'When a customer calls to cancel, what happens next determines whether you keep them. Our retention-trained agents present tailored save offers, handle objections effectively, and convert cancellation calls into continued relationships more often than you’d expect.',
        },
        {
            num: '06',
            title: 'After-Hours & Overflow Coverage',
            text: 'After-hours coverage, weekend availability, and overflow handling during peak periods so no call goes unanswered regardless of when it comes in or how many come in at once.',
        },
        {
            num: '07',
            title: 'Live Chat & Digital Support',
            text: 'Website live chat, in-app support, social media messaging, and email support managed by the same trained agents handling your voice calls; unified queue; consistent brand voice; no channel falling behind.',
        },
        {
            num: '08',
            title: 'Multilingual Support',
            text: 'Native-language agents for markets and communities where English is not the primary language. Not translation services, actual native speakers trained in your brand and protocols, serving your customers in the language they’re most comfortable in.',
        },
    ]

    const industries = [
        {
            title: 'Healthcare & Medical Practices',
            text: 'Patient scheduling, insurance verification, billing inquiries, after-hours triage, post-visit follow-up, and specialized crisis line support for behavioral health providers. Every agent is HIPAA-trained. Every interaction is handled with clinical sensitivity.',
        },
        {
            title: 'E-Commerce & Retail',
            text: 'Order management, tracking, returns, refunds, product questions, and shipping dispute resolution. Agents who know your catalogue and your policies not generalists reading from a script.',
        },
        {
            title: 'SaaS & Technology',
            text: 'Onboarding support, feature walkthroughs, tier one troubleshooting, bug reporting, and subscription billing handled by agents who actually understand software, not just customer service scripts.',
        },
        {
            title: 'Financial Services & Insurance',
            text: 'Account inquiries, policy explanations, claim status updates, billing support, and advisor scheduling with the accuracy, discretion and compliance awareness financial conversations require.',
        },
        {
            title: 'Legal & Professional Services',
            text: 'Inquiry intake, consultation scheduling, document request handling, and client communication, representing your firm with the professionalism your clients expect from the first contact.',
        },
        {
            title: 'Home Services & Field Service',
            text: '24/7 booking for trades businesses, plumbing, HVAC, electrical, landscaping, and cleaning. Emergency dispatch triage, technician scheduling, quote follow-up, and job completion satisfaction calls.',
        },
        {
            title: 'Real Estate & Property Management',
            text: 'Lead qualification, showing coordination, tenant maintenance intake, lease renewal outreach, and property availability inquiries handled so your agents spend time closing, not answering phones.',
        },
        {
            title: 'Education & Coaching',
            text: 'Enrollment inquiries, course scheduling, student support, payment plan management, and program follow-up for online education platforms, coaching practices and training organizations.',
        },
    ]

    const faqs = [
        {
            q: 'What is call center outsourcing?',
            a: 'Call center outsourcing means hiring an external team to handle customer communication, including inbound support, outbound sales, appointment scheduling and after-hours coverage on your business’s behalf, using your name and following your protocols.',
        },
        {
            q: 'How quickly can an outsourced call center go live?',
            a: 'Most businesses are fully operational with a dedicated outsourced call center team within 2 to 3 weeks, including system integration, agent training, and quality validation.',
        },
        {
            q: 'Will outsourced agents sound like they work for my company?',
            a: 'Yes, when trained properly. TechDigit agents learn your company name, products, policies, and brand voice before taking a single live call. Customers have no reason to know they’re speaking with an outsourced team.',
        },
        {
            q: 'What industries use call center outsourcing?',
            a: 'Call center outsourcing is used across virtually every industry, including healthcare, retail, technology, financial services, legal, real estate, home services, education, and more. Any business that communicates with customers by phone, chat, or email.',
        },
        {
            q: 'How does outsourcing reduce no-show rates?',
            a: 'Systematic reminder calls 24 to 48 hours before appointments, combined with immediate waitlist outreach when cancellations occur, consistently reduce no-show rates by 30 to 50 percent.',
        },
    ]

    return (
        <div className="index-six service-page service-page--call-center">
            <HeaderSix />
            <main>
                <ScrollAnimation direction="fade" delay={0.03} duration={0.38}>
                    <div className="about-page-breadcrumb">
                        <div className="container">
                            <h1 className="about-page-breadcrumb__title">Call Center & Customer Support Services</h1>
                        </div>
                    </div>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.5}>
                    <section className="td-section rts-section-gap td-service-hero">
                        <div className="container">
                            <div className="td-service-hero__panel">
                                <div className="row align-items-center g-4 g-lg-5">
                                    <div className="col-lg-6">
                                        <div className="td-service-hero__kicker">Call Center &amp; Customer Support Services</div>
                                        <h2 className="td-service-hero__title">
                                            Every Missed Call Is a Customer Who Just Called Your Competitor
                                        </h2>
                                        <p className="td-service-hero__lead">
                                            Professional, scalable call center outsourcing for businesses across every industry,
                                            with agents who answer with your name, follow your process and represent your brand
                                            the way you’d want it represented.
                                        </p>
                                        <div className="button-area">
                                            <Link to="/contactus" className="rts-btn btn-primary">
                                                Schedule Your Free Consultation
                                            </Link>
                                        </div>
                                    </div>
                                    <div className="col-lg-6">
                                        <div className="td-service-hero__visual">
                                            <img
                                                src="/assets/images/about/call%20center.jpg"
                                                alt="Service overview"
                                                loading="lazy"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="left" delay={0.08} duration={0.52}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="td-section-head td-section-head--left">
                                        <h2 className="td-section-title">
                                            Your Customers Called. Nobody Answered. Now They’re Gone.
                                        </h2>
                                        <div className="td-title-rule" aria-hidden="true" />
                                    </div>
                                    <p className="disc">
                                        It happens quietly. A potential customer calls during lunch when your team is
                                        stretched. An existing customer calls after hours with an urgent question. A patient
                                        calls while your front desk is handling three things at once.
                                    </p>
                                    <p className="disc">
                                        They reach voicemail. Or they sit on hold. Or they get through to someone who doesn’t
                                        have the answer and fumbles through it.
                                    </p>
                                    <p className="disc">
                                        Eighty-five percent of callers who reach voicemail do not call back. They move on.
                                        And you’ll never know exactly how much revenue walked out the door through
                                        unanswered calls, undertrained agents, and coverage gaps that shouldn’t exist.
                                    </p>
                                    <p className="disc">
                                        Building an in-house team to cover every hour, every volume spike, and every call type
                                        costs between $400,000 and $700,000 annually once you factor in salaries, benefits,
                                        management overhead, training, and technology. And when a trained agent leaves,
                                        which happens at a 30 to 45 percent annual rate in most call centers, that knowledge
                                        walks out the door with them.
                                    </p>
                                    <p className="disc">
                                        Outsourcing your customer communication to TechDigit typically reduces those costs by
                                        40 to 60 percent. More importantly, it means every call gets answered by someone who
                                        actually knows what they’re talking about.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.48}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="td-section-head text-center mb-5">
                                        <p className="td-section-label">Full-Spectrum Customer Communication</p>
                                        <h2 className="td-section-title">
                                            Every Channel. Every Industry. Every Call Handled Right.
                                        </h2>
                                        <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    </div>
                                </div>
                            </div>
                            <div className="row g-4">
                                {cards.map((c) => (
                                    <div key={c.num} className="col-lg-6">
                                        <div className="pillar-card h-100">
                                            <div className="pillar-card__header">
                                                <div className="pillar-card__head-main">
                                                    <div className="pillar-heading">
                                                        <span className="pillar-number">{c.num}</span>
                                                        <h4 className="pillar-title">{c.title}</h4>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="pillar-text">{c.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="right" delay={0.08} duration={0.52}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-12">
                                    <div className="td-section-head td-section-head--left">
                                        <h2 className="td-section-title">Built for Any Business That Talks to Customers</h2>
                                        <div className="td-title-rule" aria-hidden="true" />
                                    </div>
                                    <div className="row g-4 mt-1">
                                        {industries.map((it) => (
                                            <div key={it.title} className="col-md-6">
                                                <div className="service-quad-card h-100">
                                                    <h3 className="service-quad-card__title">{it.title}</h3>
                                                    <p className="service-quad-card__text">{it.text}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="fade" delay={0.04} duration={0.42}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9 text-center">
                                    <h2 className="td-section-title">Results</h2>
                                    <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    <div className="row g-4 mt-4">
                                        <div className="col-md-3 col-6">
                                            <div className="trust-stat-card h-100">
                                                <h3 className="trust-stat-value">95%+</h3>
                                                <p className="trust-stat-label">Call Answer Rate</p>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="trust-stat-card h-100">
                                                <h3 className="trust-stat-value">30–50%</h3>
                                                <p className="trust-stat-label">Average Drop</p>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="trust-stat-card h-100">
                                                <h3 className="trust-stat-value">40–60%</h3>
                                                <p className="trust-stat-label">Cost vs In-House</p>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="trust-stat-card h-100">
                                                <h3 className="trust-stat-value">Improved</h3>
                                                <p className="trust-stat-label">Customer Satisfaction</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="up" delay={0.06} duration={0.48}>
                    <section className="faq-techdigit td-section td-section--faq rts-faq-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9">
                                    <div className="faq-techdigit-inner">
                                        <div className="td-section-head text-center mb-5">
                                            <p className="td-section-label">FAQ</p>
                                            <h2 className="td-section-title">Questions People Ask</h2>
                                            <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                        </div>
                                        <div className="faq-accordion-area">
                                            <Accordion defaultActiveKey="0">
                                                {faqs.map((faq, i) => (
                                                    <Accordion.Item key={i} eventKey={String(i)}>
                                                        <Accordion.Header>{faq.q}</Accordion.Header>
                                                        <Accordion.Body>{faq.a}</Accordion.Body>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>

                <ScrollAnimation direction="down" delay={0.05} duration={0.44}>
                    <section className="td-section rts-section-gap">
                        <div className="container">
                            <div className="row justify-content-center">
                                <div className="col-lg-10 col-xl-9 text-center">
                                    <h2 className="td-section-title">Let’s Talk About the Calls You’re Currently Missing</h2>
                                    <div className="td-title-rule td-title-rule--center mx-auto" aria-hidden="true" />
                                    <p className="disc mt-3">
                                        One free conversation. We’ll look at your call volume, coverage gaps, peak hours, and
                                        customer communication challenges and show you exactly what a dedicated support
                                        team built around your business would look like.
                                    </p>
                                    <div className="button-area mt-4">
                                        <Link to="/contactus" className="rts-btn btn-primary">
                                            Schedule Your Free Consultation
                                        </Link>
                                    </div>
                                    <p className="disc mt-3">
                                        info@techdigitsolutions.com
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>
                </ScrollAnimation>
            </main>
            <FooterSix />
        </div>
    )
}

export default CallCenterService

